// @TODO [CX-663]: clean up this file when workspaces are removed

import { useCallback } from 'react';
import {
  useSurveyWidgetsQuery,
  useDeleteSurveyWidgetMutation,
  useRenameSurveyMutation,
} from '@hotjar/feedback';
import { useCurrentSite } from '@hotjar/site';
import {
  RESPONSES,
  RESPONSES_DEFAULT,
  EDIT,
  CREATE,
  RESULTS,
  PERFORMANCE,
  LIST,
} from '@hotjar/webapp/constants/routes/surveys';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { WidgetListSideBar } from '../../common/SideBar/WidgetListSideBar';
import type { Widget } from '../../common/SideBar';

export type ViewType = 'responses' | 'results' | 'performance' | 'survey-list';

function getViewRoute(view: ViewType) {
  switch (view) {
    case 'survey-list':
    case 'responses':
      return RESPONSES;
    case 'results':
      return RESULTS;
    case 'performance':
      return PERFORMANCE;
  }
}

export interface SurveySideBarProps {
  view: ViewType;
  selectedWidget: Widget | undefined;
}

export const SurveySideBar = ({ view, selectedWidget }: SurveySideBarProps): JSX.Element => {
  const currentSite = useCurrentSite().data;
  const { data: surveyWidgets } = useSurveyWidgetsQuery(currentSite?.id);
  const deleteSurveyWidgetMutation = useDeleteSurveyWidgetMutation();
  const renameSurveyMutation = useRenameSurveyMutation();

  const navigate = useNavigate();

  const hasSurveyCreatePermission = usePermission('surveys-create');
  const hasSurveyUpdatePermission = usePermission('surveys-update');
  const hasSurveyDeletePermission = usePermission('surveys-delete');

  const getWidgetRoute = useCallback(
    (widget?: Widget) => {
      if (!currentSite) throw new Error('Current site is not defined');
      if (widget)
        return getRoute(getViewRoute(view).path, { siteId: currentSite.id, surveyId: widget.id });

      return getRoute(RESPONSES_DEFAULT.path, { siteId: currentSite.id });
    },
    [currentSite, view],
  );

  const getAllWidgetsRoute = useCallback(() => {
    if (!currentSite) throw new Error('Current site is not defined');
    return getRoute(LIST.path, { siteId: currentSite.id });
  }, [currentSite]);

  const getWidgetEditRoute = useCallback(
    (widget: Widget) => {
      if (!currentSite) throw new Error('Current site is not defined');
      return getRoute(EDIT.path, { siteId: currentSite.id, surveyId: widget.id });
    },
    [currentSite],
  );

  const getWidgetDuplicateRoute = useCallback(
    (widget: Widget) => {
      if (!currentSite) throw new Error('Current site is not defined');
      return getRoute(CREATE.path, { siteId: currentSite.id, copyId: widget.id });
    },
    [currentSite],
  );

  const onDeleteWidget = useCallback(
    async (widget: Widget | null) => {
      if (!currentSite) throw new Error('Current site is not defined');
      if (!widget) throw new Error('Widget is not defined');
      await deleteSurveyWidgetMutation.mutateAsync({ siteId: currentSite.id, surveyId: widget.id });

      if (widget.id === selectedWidget?.id) {
        navigate(getWidgetRoute());
      }
    },
    [currentSite, navigate, selectedWidget?.id, getWidgetRoute, deleteSurveyWidgetMutation],
  );

  const onRenameWidget = useCallback(
    async (widgetId: number | null, name: string) => {
      if (!currentSite) throw new Error('Current site is not defined');
      if (!widgetId) throw new Error('Widget is not defined');

      await renameSurveyMutation.mutateAsync({
        siteId: currentSite.id,
        surveyId: widgetId,
        name,
      });
    },
    [currentSite, renameSurveyMutation],
  );

  return (
    <WidgetListSideBar
      widgets={surveyWidgets}
      selectedWidget={selectedWidget}
      onDeleteWidget={onDeleteWidget}
      onRenameWidget={onRenameWidget}
      getWidgetRoute={getWidgetRoute}
      getAllWidgetsRoute={getAllWidgetsRoute}
      getWidgetEditRoute={getWidgetEditRoute}
      getWidgetDuplicateRoute={getWidgetDuplicateRoute}
      widgetType="survey"
      hasDeletePermission={hasSurveyDeletePermission}
      hasDuplicatePermission={hasSurveyCreatePermission}
      hasUpdatePermission={hasSurveyUpdatePermission}
      isSurveyList={view === 'survey-list'}
    />
  );
};
